import { useEffect, useRef, useState } from "react";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { ArrowLeft, Bold, Heading2, Heading3, Italic, Link2, List, NotebookPen, Plus, Quote, Search, Trash2, X } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { htmlToText } from "@/lib/richtext";
import { edited } from "@/lib/writing";

const Tools = ({ editor }) => {
  const s = useEditorState({ editor, selector: ({ editor: e }) => ({ b: e.isActive("bold"), i: e.isActive("italic"), h2: e.isActive("heading", { level: 2 }), h3: e.isActive("heading", { level: 3 }), ul: e.isActive("bulletList"), q: e.isActive("blockquote"), a: e.isActive("link") }) });
  const c = () => editor.chain().focus();
  const link = () => { if (s.a) return c().unsetLink().run(); const h = window.prompt("Link address (https://…)"); if (h) c().setLink({ href: /^https?:\/\//.test(h) ? h : `https://${h}` }).run(); };
  const B = ({ on, f, I, id }) => <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={f} className={`h-7 w-7 rounded flex items-center justify-center ${on ? "bg-amber-500/15 text-amber-300" : "text-slate-500 hover:text-slate-200"}`} data-testid={`wnote-${id}`}><I className="w-3.5 h-3.5" /></button>;
  return (
    <div className="flex gap-0.5 border-b border-line pb-1 mb-2">
      <B on={s.b} f={() => c().toggleBold().run()} I={Bold} id="bold" /><B on={s.i} f={() => c().toggleItalic().run()} I={Italic} id="italic" />
      <B on={s.h2} f={() => c().toggleHeading({ level: 2 }).run()} I={Heading2} id="h2" /><B on={s.h3} f={() => c().toggleHeading({ level: 3 }).run()} I={Heading3} id="h3" />
      <B on={s.ul} f={() => c().toggleBulletList().run()} I={List} id="ul" /><B on={s.q} f={() => c().toggleBlockquote().run()} I={Quote} id="quote" /><B on={s.a} f={link} I={Link2} id="link" />
    </div>
  );
};

const NoteEditor = ({ note, onChange, onBack, onDelete }) => {
  const [title, setTitle] = useState(note.title);
  const [state, setState] = useState("Saved");
  const t = useRef(null), patch = useRef({});
  const save = () => { const p = patch.current; patch.current = {}; api.put(`/writing/notes/${note.id}`, p).then((r) => { onChange(r.data); setState("Saved"); }).catch((e) => { if (e.response?.status === 404) return; setState("Not saved"); toast.error(formatApiError(e)); }); };
  const queue = (p) => { patch.current = { ...patch.current, ...p }; setState("Saving…"); clearTimeout(t.current); t.current = setTimeout(save, 800); };
  useEffect(() => () => { clearTimeout(t.current); if (Object.keys(patch.current).length) save(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [2, 3] }, codeBlock: false, horizontalRule: false, link: { openOnClick: false, HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" } } })],
    content: note.html, onUpdate: ({ editor: e }) => queue({ html: e.isEmpty ? "" : e.getHTML() }),
    editorProps: { attributes: { class: "wiki-prose min-h-[40vh] focus:outline-none", "data-testid": "work-note-editor" } },
  });
  return (
    <div className="space-y-3" data-testid="work-note-open">
      <div className="flex items-center gap-2">
        <button onClick={onBack} className="p-1.5 text-slate-400 hover:text-amber-300" data-testid="work-note-back"><ArrowLeft className="w-4 h-4" /></button>
        <span className="font-code text-[10px] text-slate-500 flex-1" data-testid="work-note-status">{state}</span>
        <button onClick={onDelete} className="p-1.5 text-slate-500 hover:text-rose-300" data-testid="work-note-delete"><Trash2 className="w-4 h-4" /></button>
      </div>
      <Input value={title} maxLength={200} onChange={(e) => { setTitle(e.target.value); queue({ title: e.target.value }); }} placeholder="Untitled note" className="h-auto py-1.5 font-display !text-2xl bg-transparent border-0 border-b rounded-none px-0 focus-visible:ring-0" data-testid="work-note-title" />
      {editor && <><Tools editor={editor} /><EditorContent editor={editor} /></>}
    </div>
  );
};

const useWide = () => {
  const q = "(min-width: 1024px)";
  const [w, setW] = useState(() => window.matchMedia(q).matches);
  useEffect(() => { const m = window.matchMedia(q); const f = () => setW(m.matches); m.addEventListener("change", f); return () => m.removeEventListener("change", f); }, []);
  return w;
};

export const WorkNotesButton = ({ open, onClick }) => (
  <button onClick={onClick} aria-pressed={open} title="Work notes"
    className={`inline-flex shrink-0 items-center gap-1.5 text-xs rounded-full border px-3 py-1.5 transition-colors ${open ? "border-amber-500/60 bg-amber-500/10 text-amber-200" : "border-line text-slate-300 hover:border-amber-500/40 hover:text-amber-300"}`} data-testid="work-notes-btn">
    <NotebookPen className="w-3.5 h-3.5" /><span className="hidden sm:inline">Notes</span>
  </button>
);

const NotesPanel = ({ workId, onClose }) => {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [del, setDel] = useState(null);
  useEffect(() => { api.get(`/writing/works/${workId}/notes`).then((r) => setList(r.data)); }, [workId]);
  const create = async () => { try { const { data } = await api.post(`/writing/works/${workId}/notes`, {}); setList((l) => [data, ...l]); setSel(data.id); } catch (e) { toast.error(formatApiError(e)); } };
  const changed = (n) => setList((l) => [n, ...l.filter((x) => x.id !== n.id)]);
  const remove = async () => { try { await api.delete(`/writing/notes/${del.id}`); setList((l) => l.filter((x) => x.id !== del.id)); setSel(null); toast.success("Note deleted"); } catch (e) { toast.error(formatApiError(e)); } setDel(null); };
  const s = q.trim().toLowerCase();
  const shown = list.filter((n) => !s || n.title.toLowerCase().includes(s) || htmlToText(n.html).toLowerCase().includes(s));
  const cur = list.find((n) => n.id === sel);
  return (
    <div className="p-5" data-testid="work-notes-panel">
      <div className="flex items-start gap-2">
        <h2 className="font-display text-3xl text-amber-100 flex-1">Work notes</h2>
        {onClose && <button onClick={onClose} className="p-1.5 text-slate-500 hover:text-slate-200" title="Close notes" data-testid="work-notes-close"><X className="w-4 h-4" /></button>}
      </div>
      <p className="text-slate-400 text-xs mb-4">Plotting and research for this work. Never part of the story or word counts.</p>
      {cur ? <NoteEditor key={cur.id} note={cur} onChange={changed} onBack={() => setSel(null)} onDelete={() => setDel(cur)} /> : (
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1"><Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search notes" className="pl-9 h-9" data-testid="work-notes-search" /></div>
            <button onClick={create} className="inline-flex items-center gap-1 rounded-full bg-amber-500 hover:bg-amber-400 text-onaccent text-xs font-semibold px-4" data-testid="work-note-new"><Plus className="w-3.5 h-3.5" /> New note</button>
          </div>
          {shown.length === 0 && <p className="text-sm text-slate-500 py-6 text-center" data-testid="work-notes-empty">{list.length ? "No notes match." : "No notes yet. Start a plot outline or research page."}</p>}
          <ul className="divide-y divide-line" data-testid="work-notes-list">
            {shown.map((n) => (
              <li key={n.id}><button onClick={() => setSel(n.id)} className="w-full text-left py-3 group" data-testid={`work-note-${n.id}`}>
                <span className={`block text-sm group-hover:text-amber-300 ${n.title ? "text-slate-200" : "text-slate-400 italic"}`}>{n.title || "Untitled note"}</span>
                <span className="block text-[11px] text-slate-500 truncate">{edited(n.updated_at)} · {htmlToText(n.html).slice(0, 80) || "Empty"}</span>
              </button></li>
            ))}
          </ul>
        </div>
      )}
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="bg-panel border-line" data-testid="work-note-delete-dialog">
          <AlertDialogTitle className="font-display text-2xl text-amber-50">Delete "{del?.title || "Untitled note"}"?</AlertDialogTitle>
          <AlertDialogDescription className="text-slate-400">This note will be gone for good.</AlertDialogDescription>
          <AlertDialogFooter><AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-500 hover:bg-rose-400 text-white" data-testid="work-note-delete-confirm">Delete note</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Side panel beside the chapter on wide screens; full-screen sheet on phones and tablets
export const WorkNotes = ({ workId, open, onOpenChange }) => {
  const wide = useWide();
  if (wide) return open ? (
    <motion.aside initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25 }}
      className="w-[24rem] shrink-0 border-l border-line bg-inset sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto" data-testid="work-notes-side">
      <NotesPanel workId={workId} onClose={() => onOpenChange(false)} />
    </motion.aside>
  ) : null;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-panel border-line w-full sm:max-w-full p-0 overflow-y-auto" data-testid="work-notes-sheet">
        <SheetTitle className="sr-only">Work notes</SheetTitle><SheetDescription className="sr-only">Notes for this work</SheetDescription>
        <div className="pt-6"><NotesPanel workId={workId} /></div>
      </SheetContent>
    </Sheet>
  );
};
